import React, { createContext, useState, useContext, useEffect, useRef, ReactNode } from "react";
import authService from "../services/authService";
import type { LoginResponseUser } from "../types/api";
import { can as rbacCan } from "../utils/rbac";

interface AuthContextValue {
  user: LoginResponseUser | null;
  loading: boolean;
  isAuthenticated: boolean;
  // Capability check against the backend-computed set (UX gating only —
  // the backend enforces every verb with 403s).
  can: (capability: string) => boolean;
  login: (
    username: string,
    password: string,
    rememberMe?: boolean
  ) => Promise<{ success: boolean; error?: { code: string; message: string } }>;
  logout: () => Promise<void>;
  setUser: (user: LoginResponseUser) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<LoginResponseUser | null>(null);
  const [loading, setLoading] = useState(true);

  // Capture the SSO code synchronously at render time, before any Navigate
  // component can strip it from window.location.search via history.replace().
  const ssoCodeRef = useRef(new URLSearchParams(window.location.search).get("code"));
  // Prevents double-exchange in React 18 StrictMode (effects fire twice).
  // A second attempt on a single-use code returns 401 and would wipe the session.
  const exchangedRef = useRef(false);

  useEffect(() => {
    const run = async () => {
      const code = ssoCodeRef.current;
      if (code) {
        if (exchangedRef.current) return;
        exchangedRef.current = true;

        // Strip ?code= from the visible URL immediately (Navigate may have already done this)
        const clean = window.location.pathname + window.location.search
          .replace(/[?&]code=[^&]*/g, "")
          .replace(/^\?$/, "")
          .replace(/^&/, "?");
        window.history.replaceState(null, "", clean || window.location.pathname);

        try {
          const response = await authService.ssoExchange(code);
          if (response.success && response.data) {
            localStorage.setItem("token", response.data.token);
            localStorage.setItem("user", JSON.stringify(response.data.user));
            setUser(response.data.user);
            setLoading(false);
            return;
          }
        } catch {
          // fall through — clear state and let ProtectedRoute send to /login
        }

        // Only clear if no session was established — guards against a stale
        // duplicate call clobbering a session set by a prior successful exchange.
        if (!localStorage.getItem("token")) {
          localStorage.removeItem("user");
        }
        setLoading(false);
        return;
      }

      // Normal startup: restore session from localStorage, then refresh the
      // binding from /auth/me so role/team changes (and recomputed
      // capabilities) take effect without forcing a re-login.
      const token = localStorage.getItem("token");
      const savedUser = localStorage.getItem("user");
      if (token && savedUser) {
        try {
          setUser(JSON.parse(savedUser));
        } catch {
          // ignore malformed stored user
        }
        const fresh = await authService.me();
        if (fresh) {
          localStorage.setItem("user", JSON.stringify(fresh));
          setUser(fresh);
        }
      }
      setLoading(false);
    };
    run();
  }, []);

  const login = async (username: string, password: string, rememberMe?: boolean) => {
    try {
      const response = await authService.login(username, password, rememberMe);
      if (response.success && response.data) {
        const { token, user: userData } = response.data;
        localStorage.setItem("token", token);
        localStorage.setItem("user", JSON.stringify(userData));
        setUser(userData);
        return { success: true };
      }
      return { success: false, error: (response as any).error };
    } catch {
      return { success: false, error: { code: "NETWORK_ERROR", message: "Failed to connect to server" } };
    }
  };

  const logout = async () => {
    await authService.logout();
    setUser(null);
  };

  const can = (capability: string) => rbacCan(user, capability);

  const updateUser = (userData: LoginResponseUser) => {
    localStorage.setItem("user", JSON.stringify(userData));
    setUser(userData);
  };

  return (
    <AuthContext.Provider
      value={{ user, login, logout, loading, isAuthenticated: !!user, can, setUser: updateUser }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within an AuthProvider");
  return context;
};
